import { action, makeObservable, observable, runInAction } from "mobx";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { BaseStore } from "stores/BaseStore";
import {
    AdminModelFeedbackService,
    emptyFeedbackFilters,
} from "services/AdminModelFeedbackService";

import type {
    AdminModelFeedbackRow,
    FeedbackFilters,
} from "services/AdminModelFeedbackService";

// One instance per organization page; stale requests cannot populate another page.
export default class OrganizationModelFeedbackStore extends BaseStore {
    readonly grid = new AcxDataGridStore();
    private readonly service = new AdminModelFeedbackService();
    private requestId = 0;
    @observable filters: FeedbackFilters = { ...emptyFeedbackFilters };
    @observable total = 0;
    @observable agreements = 0;
    @observable disagreements = 0;
    @observable error = "";
    @observable selected: AdminModelFeedbackRow | null = null;

    constructor(readonly organizationId: string) {
        super("OrganizationModelFeedbackStore");
        makeObservable(this);
        this.grid.disableLocalConfig = true;
        this.grid.hideHeader = true;
        this.grid.heightOverride = "540px";
        this.grid.checkboxSelection = false;
        this.grid.disableRowSelectionOnClick = true;
        this.grid.pagination = true;
        this.grid.paginationMode = "server";
        this.grid.sortingMode = "server";
        this.grid.filterMode = "server";
        this.grid.paginationModel = { page: 0, pageSize: 25 };
        this.grid.pageSizeOptions = [25, 50, 100];
        this.grid.rowCount = 0;
        this.grid.sortModel = [{ field: "modifiedOn", sort: "desc" }];
        this.grid.onPaginationModelChange = (model) => {
            runInAction(() => {
                this.grid.paginationModel = model;
            });
            void this.load();
        };
        this.grid.sortRequest = () => {
            runInAction(() => {
                this.grid.paginationModel = {
                    ...this.grid.paginationModel,
                    page: 0,
                };
            });
            void this.load();
        };
    }

    @action applyFilters(filters: FeedbackFilters) {
        this.filters = { ...filters };
        this.grid.paginationModel = { ...this.grid.paginationModel, page: 0 };
        this.selected = null;
        void this.load();
    }

    @action select(row: AdminModelFeedbackRow | null) {
        this.selected = row;
    }

    @action dispose() {
        this.requestId++;
    }

    @action async load() {
        const requestId = ++this.requestId;
        this.grid.isLoading = true;
        this.error = "";
        const { page, pageSize } = this.grid.paginationModel;
        const sort = this.grid.sortModel?.[0];
        try {
            const result = await this.service.getPage(
                this.organizationId,
                page,
                pageSize,
                sort?.field ?? "modifiedOn",
                (sort?.sort ?? "desc") === "desc",
                this.filters,
            );
            if (requestId !== this.requestId) return;
            runInAction(() => {
                this.grid.setRows(result.items);
                this.grid.rowCount = this.total = result.total;
                this.agreements = result.agreements;
                this.disagreements = result.disagreements;
            });
            // A deletion can remove the last page while an admin is viewing it.
            if (page > 0 && page * pageSize >= result.total) {
                runInAction(() => {
                    this.grid.paginationModel = { page: 0, pageSize };
                });
                await this.load();
            }
        } catch {
            if (requestId !== this.requestId) return;
            runInAction(() => {
                this.error = "Unable to load model feedback. Please try again.";
                this.grid.setRows([]);
                this.grid.rowCount =
                    this.total =
                    this.agreements =
                    this.disagreements =
                        0;
            });
        } finally {
            if (requestId === this.requestId)
                runInAction(() => {
                    this.grid.isLoading = false;
                });
        }
    }
}
